import { fromTime } from 'utils/validations/time';
import { DateTime } from 'luxon';
import { EnumMap } from '@instinet-analytics/core-lib';
import { ValueType } from '@instinet-analytics/semantic-ui-components';
import {
    isPeriod,
    periodValueDisplay,
} from '@instinet-analytics/semantic-ui-components';
import { TodayValue } from '@instinet-analytics/core-lib';

const dateFormat = 'dd/MM/yy';
// TODO: Set the date format globally somewhere

export enum ExactSign {
    EQ = 'EQ',
    NEQ = 'NEQ',
}

export enum BoundarySign {
    LT = 'LT',
    LTE = 'LTE',
    GT = 'GT',
    GTE = 'GTE',
}

/*
 * Comparison for a list of string values
 */
export enum MembershipSign {
    INCLUDE = 'INCLUDE',
    EXCLUDE = 'EXCLUDE',
    CONTAINS = 'CONTAINS',
    NOTCONTAINS = 'NOTCONTAINS',
    STARTSWITH = 'STARTSWITH',
    ENDSWITH = 'ENDSWITH',
    BLANK = 'BLANK',
    NOTBLANK = 'NOTBLANK',
}

export const signDoesNotNeedValue = (sign?: MembershipSign) =>
    sign === MembershipSign.BLANK || sign === MembershipSign.NOTBLANK;

// Typescript doesn't like union types as index types, so we need to cast this as any here.
// see https://github.com/microsoft/typescript/issues/24220
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signTextMap: any = {
    [BoundarySign.LT]: '<',
    [BoundarySign.LTE]: '≤',
    [ExactSign.EQ]: '=',
    [ExactSign.NEQ]: '≠',
    [MembershipSign.INCLUDE]: '=',
    [MembershipSign.EXCLUDE]: '≠',
    [MembershipSign.CONTAINS]: 'contains',
    [MembershipSign.NOTCONTAINS]: 'does not contain',
    [MembershipSign.BLANK]: 'is blank',
    [MembershipSign.NOTBLANK]: 'is not blank',
    [MembershipSign.STARTSWITH]: 'starts with',
    [MembershipSign.ENDSWITH]: 'ends with',
    [BoundarySign.GT]: '>',
    [BoundarySign.GTE]: '≥',
};

export const isExactSign = (
    sign: ExactSign | BoundarySign
): sign is ExactSign => sign === ExactSign.EQ || sign === ExactSign.NEQ;

export const isBoundarySign = (
    sign: ExactSign | BoundarySign
): sign is BoundarySign =>
    sign === BoundarySign.GT ||
    sign === BoundarySign.GTE ||
    sign === BoundarySign.LT ||
    sign === BoundarySign.LTE;

export const exactOrBoundarySigns = [
    ExactSign.EQ,
    ExactSign.NEQ,
    BoundarySign.GT,
    BoundarySign.GTE,
    BoundarySign.LT,
    BoundarySign.LTE,
];

export const getOppositeBoundarySign = (sign: BoundarySign) => {
    switch (sign) {
        case BoundarySign.LT:
            return BoundarySign.GT;
        case BoundarySign.LTE:
            return BoundarySign.GTE;
        case BoundarySign.GT:
            return BoundarySign.LT;
        case BoundarySign.GTE:
            return BoundarySign.LTE;
    }
};

export const getSupportedOppositeBoundarySigns = (sign: BoundarySign) => {
    switch (sign) {
        case BoundarySign.LT:
            return [BoundarySign.GT, BoundarySign.GTE];
        case BoundarySign.LTE:
            return [BoundarySign.GTE, BoundarySign.GT];
        case BoundarySign.GT:
            return [BoundarySign.LT, BoundarySign.LTE];
        case BoundarySign.GTE:
            return [BoundarySign.LTE, BoundarySign.LT];
    }
};

// For filters, "Currency" is treated like a "Number"
export type FilterValueType = Exclude<ValueType, 'CURRENCY'>;

export function mapValueTypeToFilterValueType(
    valueType: ValueType
): FilterValueType {
    if (valueType === ValueType.CURRENCY) {
        return ValueType.NUMBER;
    }
    return valueType;
}

export function filterValueTypeHandlesValueType(
    filterValueType: FilterValueType,
    columnValueType: ValueType
): boolean {
    return mapValueTypeToFilterValueType(columnValueType) === filterValueType;
}

export interface ExactFilter<T> {
    exactSign: ExactSign;
    valueType: FilterValueType;
    value: T;
}

export type TodayFilter = ExactFilter<TodayValue>;

export interface RangeFilter<T> {
    valueType: FilterValueType;
    lowerSign?: BoundarySign.GT | BoundarySign.GTE;
    lowerValue?: T;
    upperSign?: BoundarySign.LT | BoundarySign.LTE;
    upperValue?: T;
}

export interface MembershipFilter<T> {
    valueType: FilterValueType;
    membershipSign: MembershipSign;
    values: T[];
}

export type Filter<T> =
    | ExactFilter<T>
    | RangeFilter<T>
    | MembershipFilter<T>
    | TodayFilter;

export function isExactFilter<T>(filter: Filter<T>): filter is ExactFilter<T> {
    return 'exactSign' in filter;
}

export function isRangeFilter<T>(filter: Filter<T>): filter is RangeFilter<T> {
    return 'lowerSign' in filter || 'upperSign' in filter;
}

export function isMembershipFilter<T>(
    filter: Filter<T>
): filter is MembershipFilter<T> {
    return 'membershipSign' in filter;
}

export function formatFilterValue<T>(
    valueType: FilterValueType,
    value: T,
    enumMap?: EnumMap
) {
    switch (valueType) {
        case ValueType.TIME:
            return fromTime(value as unknown as number).join(':');
        case ValueType.DATETIME:
        case ValueType.DATE: {
            if (typeof value === 'string') {
                return value;
            }
            if (isPeriod(value)) {
                return periodValueDisplay(value);
            }
            return DateTime.fromMillis(value as unknown as number).toFormat(
                dateFormat
            );
        }
        case ValueType.ENUM:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (enumMap as any)[value] as string;
        default:
            return value;
    }
}
